import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import type { SavedPlate } from "ipctg-inflow-types/InFlowDB"
import { useState } from "react"
import { Radio, RadioGroup } from "react-aria-components"
import { Controller, useForm, useWatch } from "react-hook-form"
import { Trans, useTranslation } from "react-i18next"

import { Button } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Label from "@/globalComponents/atoms/Label"
import Text from "@/globalComponents/atoms/Text"
import Modal from "@/globalComponents/molecules/Modal"
import TextField from "@/globalComponents/molecules/TextField"
import { queryClient } from "@/router"
import { licensePlateValidationRule, requiredRule } from "@/utilities/form"
import {
  reserveCheckoutQueryOptions,
  reservePaymentIntentQueryOptions,
  submitReservePlate,
} from "@/utilities/hooks/queries/reserve"
import { meQueryOptions } from "@/utilities/hooks/queries/user"
import { useErrorModal } from "@/utilities/hooks/useErrorModal"
import useToggle from "@/utilities/hooks/useToggle"
import { log } from "@/utilities/logging"
import { normalizeLicensePlate } from "@/utilities/normalizers"

import styles from "./vehicles.module.css"

export const Route = createFileRoute(
  "/qr/$qrLookupID/reserve/$epoch/calendars/$calendarID/dates/$year/$month/$day/vehicles"
)({
  staticData: {
    pageName: "Vehicle",
  },
  component: RouteComponent,
})

const LATER_VALUE = "__later__"
const NEW_PLATE_VALUE = ""

interface VehicleFormSchema {
  selectedPlate: string
  licensePlate: string
}

function RouteComponent() {
  const navigate = Route.useNavigate()
  const { t } = useTranslation(["reserve", "errors"])
  const { setError } = useErrorModal()
  const [isAddLaterInfoOpen, toggleAddLaterInfo] = useToggle(false)
  const { data: me } = useSuspenseQuery(meQueryOptions())

  // TODO make this session based
  const [
    hasSeenAddVehicleLaterDisclaimer,
    setHasSeenAddVehicleLaterDisclaimer,
  ] = useState(false)

  const submitPlateMutation = useMutation({
    mutationFn: submitPlateMutationFn,
    onError,
  })

  const savedPlates: SavedPlate[] = me?.inflowUser.savedPlates || []
  const hasSavedPlates = Boolean(savedPlates.length)

  const defaultPlate =
    savedPlates.length > 0 ? savedPlates[0].plate : NEW_PLATE_VALUE

  const form = useForm<VehicleFormSchema>({
    defaultValues: {
      selectedPlate: defaultPlate,
      licensePlate: "",
    },
  })

  const [selectedPlate] = useWatch({
    control: form.control,
    name: ["selectedPlate"],
  })

  const isEnteringNewPlate = selectedPlate === NEW_PLATE_VALUE

  return (
    <>
      <Card>
        <Heading level={1}>{t("vehicles.heading")}</Heading>
        <Text level={2} style={{ marginTop: "var(--space-2)" }}>
          {t("vehicles.description", { count: savedPlates.length })}
        </Text>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Controller
            name="selectedPlate"
            control={form.control}
            render={({ field }) => (
              <RadioGroup
                value={field.value}
                onChange={(value) => {
                  if (
                    value === LATER_VALUE &&
                    !hasSeenAddVehicleLaterDisclaimer
                  ) {
                    toggleAddLaterInfo()
                  }
                  field.onChange(value)
                }}
                className={styles.vehicleList}
                aria-label={t("vehicles.savedPlates")}
              >
                {hasSavedPlates && <Label>{t("vehicles.savedPlates")}</Label>}
                {savedPlates.map((vehicle) => (
                  <Radio
                    key={vehicle.plate}
                    value={vehicle.plate}
                    className={styles.vehicleItem}
                    aria-label={
                      hasSavedPlates
                        ? undefined
                        : t("common.selectAnOption", { ns: "common" })
                    }
                  >
                    <div className="radio" data-hue="neutral">
                      <div className="selectedIndicator" />
                    </div>
                    <div className={styles.vehicleInfo}>
                      <Icon name="car" />
                      <Text
                        level={2}
                        className={`semi-bold start ${styles.vehicleText}`}
                      >
                        {vehicle.nickname ? (
                          <>
                            {vehicle.nickname}&nbsp;
                            <Text as="span" level={2} className="regular">
                              ({vehicle.plate})
                            </Text>
                          </>
                        ) : (
                          vehicle.plate
                        )}
                      </Text>
                    </div>
                  </Radio>
                ))}
                <Radio value={LATER_VALUE} className={styles.vehicleItem}>
                  <div className="radio" data-hue="neutral">
                    <div className="selectedIndicator" />
                  </div>
                  <Text level={2} className="semi-bold start">
                    {t("vehicles.addLater")}
                  </Text>
                  <Button
                    buttonType="default"
                    className={styles.infoButton}
                    aria-label={t("vehicles.addLaterInfo.title")}
                    onPress={() => {
                      toggleAddLaterInfo()
                    }}
                  >
                    <Icon name="infoOutline" className={styles.infoIcon} />
                  </Button>
                </Radio>
                <Radio
                  value={NEW_PLATE_VALUE}
                  className={styles.vehicleItem}
                  style={{ marginBottom: "var(--space-5)" }}
                >
                  <div className="radio" data-hue="neutral">
                    <div className="selectedIndicator" />
                  </div>
                  <Text level={2} className="semi-bold start">
                    {t(
                      hasSavedPlates
                        ? "vehicles.enterNewPlate"
                        : "vehicles.enterPlate"
                    )}
                  </Text>
                </Radio>
              </RadioGroup>
            )}
          />
          {isEnteringNewPlate && (
            <Controller
              name="licensePlate"
              control={form.control}
              rules={{
                required: requiredRule(t("common.required", { ns: "errors" })),
                validate: (value) => {
                  const normalized = normalizeLicensePlate(value)
                  return licensePlateValidationRule(t)(normalized)
                },
              }}
              render={({
                field: { ref, value, onChange, ...rest },
                fieldState: { error, invalid },
              }) => (
                <TextField
                  {...rest}
                  isInvalid={invalid}
                  onChange={(v) => onChange(normalizeLicensePlate(v))}
                  value={typeof value === "string" ? value : ""}
                  style={{ marginBottom: "var(--space-5)" }}
                  aria-label={t("vehicles.licensePlate")}
                >
                  <Label>{t("vehicles.licensePlate")}</Label>
                  <TextField.Input
                    innerRef={ref}
                    placeholder={t("vehicles.licensePlatePlaceholder")}
                    maxLength={12}
                    style={{ textTransform: "uppercase" }}
                  />
                  <FieldError>{error?.message}</FieldError>
                </TextField>
              )}
            />
          )}
          <Button
            type="submit"
            buttonType="primary"
            isPending={submitPlateMutation.isPending}
          >
            {t("vehicles.continue")}
          </Button>
        </form>
      </Card>
      <Modal
        modalStyle="centered"
        isOpen={isAddLaterInfoOpen}
        onOpenChange={() => {
          setHasSeenAddVehicleLaterDisclaimer(true)
          toggleAddLaterInfo()
        }}
      >
        <Modal.Header>
          <Heading level={2} slot="title">
            {t("vehicles.addLaterInfo.title")}
          </Heading>
        </Modal.Header>
        <Modal.Body>
          <Trans
            ns="reserve"
            i18nKey="vehicles.addLaterInfo.body"
            components={[
              <Text level={1} key="first" />,
              <Text level={1} key="second" />,
            ]}
          />
        </Modal.Body>
      </Modal>
    </>
  )

  function onSubmit(data: VehicleFormSchema) {
    queryClient.resetQueries(reserveCheckoutQueryOptions())
    queryClient.resetQueries(reservePaymentIntentQueryOptions())

    submitPlateMutation.mutate(data)
  }

  function onError(error: Error, data: VehicleFormSchema) {
    const plate =
      data.selectedPlate === LATER_VALUE
        ? null
        : data.selectedPlate || data.licensePlate || null
    log({
      level: "error",
      flow: "reserve",
      scope: "fetchResponse.submitPlate",
      message: "Failed to submit license plate",
      meta: { error, plate },
    })
    setError({ error })
  }

  async function submitPlateMutationFn(data: VehicleFormSchema) {
    const plate =
      data.selectedPlate === LATER_VALUE
        ? null
        : data.selectedPlate || data.licensePlate || null
    await submitReservePlate({
      plate,
      plateEntrySkipped: data.selectedPlate === LATER_VALUE,
    })
    await navigate({
      to: "/qr/$qrLookupID/reserve/$epoch/calendars/$calendarID/dates/$year/$month/$day/cart",
    })
  }
}
